import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { AppShell } from '@/components/layout/AppShell';
import { Card } from '@/components/ui/Card';
import { Input } from '@/components/ui/Input';
import { Label } from '@/components/ui/Label';
import { Button } from '@/components/ui/Button';
import { Badge } from '@/components/ui/Badge';
import { PasswordStrengthMeter } from '@/components/ui/PasswordStrengthMeter';
import { Select } from '@/components/ui/Select';
import { useAuth } from '@/hooks/useAuth';
import {
  useChangePassword,
  useMe,
  useUpdateOOO,
  useUpdateProfile,
  useUsers,
} from '@/api/users';
import { usePageTitle } from '@/hooks/usePageTitle';
import { useToast } from '@/hooks/useToast';
import { useTourStore } from '@/stores/tour';
import { apiError, api } from '@/lib/api';
import { format } from 'date-fns';
import { MonitorSmartphone, RotateCcw, ShieldCheck, ShieldOff } from 'lucide-react';
import { MfaSetupModal } from '@/components/auth/MfaSetupModal';
import type { Role } from '@/types/api';
import { useSessions, useRevokeSession } from '@/api/sessions';
import { Spinner } from '@/components/ui/Spinner';
import { cn } from '@/lib/cn';

const ROLE_LABEL: Record<string, string> = {
  sysadmin: 'System admin',
  manager: 'Manager',
  staff: 'Staff',
  client_admin: 'Client admin',
  client_user: 'Client user',
};

const ROLE_CAPABILITIES: Record<Role, string> = {
  sysadmin:
    "Full access. Manage organizations, users, folder templates, request templates, and system settings. View all audit events.",
  manager:
    "Oversee all assigned organizations. Invite staff + clients. Create file requests. Review activity across teams.",
  staff:
    "Access organizations you're assigned to. Upload/download files. Create file requests for clients. Receive notifications for your assignments.",
  client_admin:
    "Full access to your organization's workspace. Invite other users from your organization. Upload and download files. Respond to file requests from BillerBay.",
  client_user:
    "Access your organization's workspace. Upload and download files. Respond to file requests from BillerBay.",
};

const STAFF_ROLES: Role[] = ['sysadmin', 'manager', 'staff'];

function isStaffRole(role: Role | undefined): boolean {
  if (!role) return false;
  return STAFF_ROLES.includes(role);
}

type Tab = 'profile' | 'security' | 'sessions' | 'out-of-office';

export function ProfilePage() {
  usePageTitle('Profile');
  const { user, setUser } = useAuth();
  const me = useMe();
  const [searchParams, setSearchParams] = useSearchParams();
  const rawTab = searchParams.get('tab') as Tab | null;
  const effectiveUser = me.data ?? user;
  const staff = isStaffRole(effectiveUser?.role);

  const TABS: { id: Tab; label: string }[] = [
    { id: 'profile', label: 'Profile' },
    { id: 'security', label: 'Security' },
    { id: 'sessions', label: 'Sessions' },
    ...(staff ? [{ id: 'out-of-office' as Tab, label: 'Out of office' }] : []),
  ];

  const tab: Tab = TABS.some((t) => t.id === rawTab) ? rawTab! : 'profile';
  const setTab = (t: Tab) => setSearchParams({ tab: t });

  return (
    <AppShell crumbs={[{ label: 'Profile & Settings' }]}>
      <div className="max-w-2xl">
        {/* Tab bar */}
        <div className="flex gap-1 border-b border-[#E3E3F1] mb-6">
          {TABS.map((t) => (
            <button
              key={t.id}
              type="button"
              onClick={() => setTab(t.id)}
              className={cn(
                'px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors',
                tab === t.id
                  ? 'border-[#0410BD] text-[#0410BD]'
                  : 'border-transparent text-slate-500 hover:text-slate-800',
              )}
            >
              {t.label}
            </button>
          ))}
        </div>

        {tab === 'profile' && <ProfileTab user={effectiveUser} me={me} setUser={setUser} />}
        {tab === 'security' && <SecurityTab />}
        {tab === 'sessions' && <SessionsTab />}
        {tab === 'out-of-office' && staff && <OOOTab effectiveUser={effectiveUser} />}
      </div>
    </AppShell>
  );
}

function ProfileTab({ user: effectiveUser, me, setUser }: { user: ReturnType<typeof useMe>['data'] | null | undefined; me: ReturnType<typeof useMe>; setUser: (u: any) => void }) {
  const updateProfile = useUpdateProfile();
  const toast = useToast();
  const navigate = useNavigate();
  const { hasRole } = useAuth();
  const startTour = useTourStore((s) => s.startTour);
  const startAdminTour = useTourStore((s) => s.startAdminTour);
  const isAdmin = hasRole(['sysadmin', 'manager']);

  const [fullName, setFullName] = useState(effectiveUser?.full_name ?? '');
  useEffect(() => {
    if (me.data) setFullName(me.data.full_name);
  }, [me.data]);

  const handleSaveDetails = async () => {
    const trimmed = fullName.trim();
    if (!trimmed) { toast.error('Name cannot be empty'); return; }
    try {
      const updated = await updateProfile.mutateAsync({ full_name: trimmed });
      setUser(updated);
      toast.success('Profile updated');
    } catch (e) {
      toast.error('Update failed', apiError(e));
    }
  };

  const handleRestartTour = () => {
    try { localStorage.removeItem('bb.tour_completed'); } catch { /* ignore */ }
    startTour();
    navigate('/');
  };

  const handleRestartAdminTour = () => {
    try { localStorage.removeItem('bb.admin_tour_completed'); } catch { /* ignore */ }
    startAdminTour();
    navigate('/admin/organizations');
  };

  const capabilityText = effectiveUser?.role ? ROLE_CAPABILITIES[effectiveUser.role] : null;
  const capabilityBullets = capabilityText
    ? capabilityText.split('. ').map((s) => s.trim().replace(/\.$/, '')).filter((s) => s.length > 0)
    : [];

  return (
    <div className="space-y-6">
      {effectiveUser && capabilityText && (
        <Card title="Your role">
          <div className="flex items-center gap-2 mb-3">
            <Badge tone="blue">{ROLE_LABEL[effectiveUser.role] ?? effectiveUser.role}</Badge>
          </div>
          <ul className="list-disc pl-5 space-y-1 text-sm text-slate-700">
            {capabilityBullets.map((line) => (
              <li key={line}>{line}.</li>
            ))}
          </ul>
        </Card>
      )}

      <Card title="Account details">
        <dl className="grid grid-cols-[140px_1fr] gap-y-3 text-sm mb-5">
          <dt className="text-slate-500">Email</dt>
          <dd className="text-slate-800">{effectiveUser?.email}</dd>

          <dt className="text-slate-500">Role</dt>
          <dd><Badge tone="blue">{ROLE_LABEL[effectiveUser?.role ?? ''] ?? effectiveUser?.role}</Badge></dd>

          <dt className="text-slate-500">Status</dt>
          <dd>
            {effectiveUser?.is_disabled
              ? <Badge tone="red">Disabled</Badge>
              : <Badge tone="green">Active</Badge>}
          </dd>

          <dt className="text-slate-500">Last login</dt>
          <dd className="text-slate-800">
            {effectiveUser?.last_login_at ? format(new Date(effectiveUser.last_login_at), 'PP p') : '—'}
          </dd>

          <dt className="text-slate-500">Member since</dt>
          <dd className="text-slate-800">
            {effectiveUser?.created_at ? format(new Date(effectiveUser.created_at), 'PP') : '—'}
          </dd>
        </dl>

        <div className="border-t border-slate-100 pt-4">
          <Label htmlFor="p-name">Display name</Label>
          <div className="flex items-center gap-2 mt-1">
            <Input
              id="p-name"
              value={fullName}
              onChange={(e) => setFullName(e.target.value)}
              className="flex-1"
            />
            <Button
              variant="primary"
              onClick={handleSaveDetails}
              loading={updateProfile.isPending}
              disabled={!fullName.trim() || fullName.trim() === effectiveUser?.full_name}
              className="w-auto"
            >
              Save
            </Button>
          </div>
        </div>
      </Card>

      <Card title="Guided tour">
        <p className="text-sm text-slate-500 mb-3">Restart the guided tour to revisit the walkthrough.</p>
        <div className="flex items-center gap-2">
          <Button variant="secondary" onClick={handleRestartTour} className="text-sm">
            <RotateCcw size={14} /> Restart tour
          </Button>
          {isAdmin && (
            <Button variant="secondary" onClick={handleRestartAdminTour} className="text-sm">
              <RotateCcw size={14} /> Restart admin tour
            </Button>
          )}
        </div>
      </Card>
    </div>
  );
}

function SecurityTab() {
  const { user } = useAuth();
  const changePassword = useChangePassword();
  const toast = useToast();

  const [currentPwd, setCurrentPwd] = useState('');
  const [newPwd, setNewPwd] = useState('');
  const [confirmPwd, setConfirmPwd] = useState('');
  const [pwdError, setPwdError] = useState<string | null>(null);

  const [mfaPassword, setMfaPassword] = useState('');
  const [mfaDisabling, setMfaDisabling] = useState(false);
  const [mfaError, setMfaError] = useState<string | null>(null);
  const [showMfaSetup, setShowMfaSetup] = useState(false);
  const me = useMe();

  const handleChangePassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setPwdError(null);
    if (newPwd.length < 10) { setPwdError('New password must be at least 10 characters.'); return; }
    if (newPwd !== confirmPwd) { setPwdError('New passwords do not match.'); return; }
    try {
      await changePassword.mutateAsync({ current_password: currentPwd, new_password: newPwd });
      toast.success('Password changed');
      setCurrentPwd(''); setNewPwd(''); setConfirmPwd('');
    } catch (err) {
      setPwdError(apiError(err));
    }
  };

  const handleDisableMfa = async (e: React.FormEvent) => {
    e.preventDefault();
    setMfaError(null);
    setMfaDisabling(true);
    try {
      await api.delete('/auth/mfa', { data: { password: mfaPassword } });
      toast.success('Two-factor authentication disabled');
      setMfaPassword('');
      me.refetch();
    } catch (err) {
      setMfaError(apiError(err));
    } finally {
      setMfaDisabling(false);
    }
  };

  const mfaEnabled = me.data?.mfa_enabled ?? user?.mfa_enabled ?? false;

  return (
    <div className="space-y-6">
      <Card title="Change password">
        <form onSubmit={handleChangePassword} className="space-y-3" noValidate>
          <div>
            <Label htmlFor="p-current">Current password</Label>
            <Input id="p-current" type="password" autoComplete="current-password" value={currentPwd} onChange={(e) => setCurrentPwd(e.target.value)} />
          </div>
          <div>
            <Label htmlFor="p-new">New password</Label>
            <Input id="p-new" type="password" autoComplete="new-password" value={newPwd} onChange={(e) => setNewPwd(e.target.value)} />
            <PasswordStrengthMeter password={newPwd} />
            <p className="text-xs text-slate-500 mt-1">At least 10 characters.</p>
          </div>
          <div>
            <Label htmlFor="p-confirm">Confirm new password</Label>
            <Input id="p-confirm" type="password" autoComplete="new-password" value={confirmPwd} onChange={(e) => setConfirmPwd(e.target.value)} />
          </div>
          {pwdError && <div className="text-sm text-red-600 bg-red-50 border border-red-100 px-3 py-2">{pwdError}</div>}
          <Button type="submit" variant="primary" loading={changePassword.isPending} disabled={!currentPwd || !newPwd || !confirmPwd} className="w-auto">
            Change password
          </Button>
        </form>
      </Card>

      <Card title="Two-factor authentication">
        {mfaEnabled ? (
          <div>
            <div className="flex items-center gap-2 mb-3">
              <Badge tone="green">Enabled</Badge>
              <span className="text-sm text-slate-500">Your account is protected with an authenticator app.</span>
            </div>
            <form onSubmit={handleDisableMfa} className="space-y-3" noValidate>
              <p className="text-sm text-slate-600">To disable 2FA, confirm your current password:</p>
              <div>
                <Label htmlFor="mfa-pwd">Current password</Label>
                <Input id="mfa-pwd" type="password" autoComplete="current-password" value={mfaPassword} onChange={(e) => setMfaPassword(e.target.value)} />
              </div>
              {mfaError && <div className="text-sm text-red-600 bg-red-50 border border-red-100 px-3 py-2">{mfaError}</div>}
              <Button type="submit" variant="danger" loading={mfaDisabling} disabled={!mfaPassword} className="w-auto">
                <ShieldOff size={14} /> Disable 2FA
              </Button>
            </form>
          </div>
        ) : (
          <div>
            <div className="flex items-center gap-2 mb-3">
              <Badge tone="gray">Not enabled</Badge>
              <span className="text-sm text-slate-500">Your account is not protected with 2FA.</span>
            </div>
            <Button variant="primary" onClick={() => setShowMfaSetup(true)} className="w-auto text-xs">
              <ShieldCheck size={14} /> Set up 2FA
            </Button>
          </div>
        )}
      </Card>

      {showMfaSetup && (
        <MfaSetupModal
          onDone={() => { setShowMfaSetup(false); me.refetch(); }}
          allowSkip
        />
      )}
    </div>
  );
}

function SessionsTab() {
  const sessions = useSessions();
  const revoke = useRevokeSession();
  const toast = useToast();

  const handleRevoke = async (id: string) => {
    try {
      await revoke.mutateAsync(id);
      toast.success('Session revoked');
    } catch (e) {
      toast.error('Failed to revoke session', apiError(e));
    }
  };

  return (
    <Card title={<span className="inline-flex items-center gap-2"><MonitorSmartphone size={16} className="text-[#676687]" /> Active sessions</span>}>
      {sessions.isLoading ? (
        <div className="flex justify-center py-6"><Spinner /></div>
      ) : (sessions.data?.length ?? 0) === 0 ? (
        <p className="text-sm text-slate-500">No active sessions found.</p>
      ) : (
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-xs uppercase tracking-wide text-[#676687] border-b border-[#E3E3F1]">
              <th className="py-2 pr-4 font-semibold">IP address</th>
              <th className="py-2 pr-4 font-semibold">Logged in</th>
              <th className="py-2 pr-4 font-semibold">Expires</th>
              <th className="py-2 font-semibold text-right">Action</th>
            </tr>
          </thead>
          <tbody>
            {(sessions.data ?? []).map((s) => (
              <tr key={s.id} className="border-b border-[#F2F2F8] last:border-0">
                <td className="py-2 pr-4 text-[#12122C] font-mono text-xs">{s.ip_address ?? <span className="text-slate-400 font-sans">—</span>}</td>
                <td className="py-2 pr-4 text-[#676687]">{format(new Date(s.created_at), 'PP p')}</td>
                <td className="py-2 pr-4 text-[#676687]">{format(new Date(s.expires_at), 'PP p')}</td>
                <td className="py-2 text-right">
                  {s.is_current ? (
                    <Badge tone="green">Current</Badge>
                  ) : (
                    <Button variant="ghost" onClick={() => handleRevoke(s.id)} disabled={revoke.isPending} className="text-xs text-red-600 hover:bg-red-50 border-red-200">
                      Revoke
                    </Button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </Card>
  );
}

function OOOTab({ effectiveUser }: { effectiveUser: any }) {
  const updateOOO = useUpdateOOO();
  const toast = useToast();
  const { user } = useAuth();

  const staffUsersQuery = useUsers({ limit: 500, offset: 0 });
  const backupCandidates = (staffUsersQuery.data?.items ?? []).filter(
    (u) => STAFF_ROLES.includes(u.role) && !u.is_disabled && u.id !== (effectiveUser?.id ?? user?.id),
  );

  const [isOoo, setIsOoo] = useState<boolean>(false);
  const [backupId, setBackupId] = useState<string>('');
  const [oooNote, setOooNote] = useState<string>('');

  useEffect(() => {
    if (!effectiveUser) return;
    setIsOoo(effectiveUser.is_ooo ?? false);
    setBackupId(effectiveUser.ooo_backup_user_id ?? '');
    setOooNote(effectiveUser.ooo_note ?? '');
  }, [effectiveUser?.is_ooo, effectiveUser?.ooo_backup_user_id, effectiveUser?.ooo_note]);

  const handleSaveOOO = async () => {
    try {
      await updateOOO.mutateAsync({
        is_ooo: isOoo,
        backup_user_id: isOoo && backupId ? backupId : null,
        note: isOoo ? (oooNote.trim() || null) : null,
      });
      toast.success('Out of office updated');
    } catch (e) {
      toast.error('Update failed', apiError(e));
    }
  };

  return (
    <Card title="Out of office">
      <p className="text-xs text-slate-500 mb-4">
        While you're out, your name still appears on file requests but a backup contact is shown to clients alongside.
      </p>
      <div className="space-y-4">
        <label className="flex items-center gap-2 text-sm text-slate-800 cursor-pointer">
          <input type="checkbox" checked={isOoo} onChange={(e) => setIsOoo(e.target.checked)} className="h-4 w-4 accent-bb-navy" />
          <span>I'm out of office</span>
        </label>

        {isOoo && (
          <>
            <div>
              <Label htmlFor="p-ooo-backup">Backup person (optional)</Label>
              <Select
                id="p-ooo-backup"
                value={backupId}
                onChange={setBackupId}
                disabled={staffUsersQuery.isLoading}
                options={backupCandidates.map((u) => ({ value: u.id, label: `${u.full_name} (${ROLE_LABEL[u.role] ?? u.role})` }))}
                placeholder="-- None --"
              />
              <p className="text-xs text-slate-500 mt-1">Only staff can be listed as a backup.</p>
            </div>
            <div>
              <Label htmlFor="p-ooo-note">Note (optional)</Label>
              <textarea
                id="p-ooo-note"
                value={oooNote}
                onChange={(e) => setOooNote(e.target.value.slice(0, 255))}
                rows={3}
                maxLength={255}
                className="w-full border border-bb-lilac bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-bb-navy/30 focus:border-bb-navy"
                placeholder="Back on Monday, please contact…"
              />
            </div>
          </>
        )}

        <Button variant="primary" onClick={handleSaveOOO} loading={updateOOO.isPending} className="w-auto">
          Save
        </Button>
      </div>
    </Card>
  );
}
